import type { ReactElement, VoidFunctionComponent } from 'react';
import { useRef, useState } from 'react';
import * as _ from 'lodash';
import cx from 'classnames';
import styles from './DynamicShowMoreList.module.less';
import Button, { BUTTON_TYPE } from '../Button';
import { useToggle } from '../../../hooks';
import VisibilityWrapper from './VisibilityWrapper';

type ShowMoreButtonProps = {
  isShowing: boolean;
  toggleIsShowing: () => void;
};

const ShowMoreButton: VoidFunctionComponent<ShowMoreButtonProps> = ({ isShowing, toggleIsShowing }) => (
  <span className={styles.showMoreButton}>
    (
    <Button type={BUTTON_TYPE.ANCHOR} onClick={toggleIsShowing}>
      {isShowing ? 'Show less' : 'Show more'}
    </Button>
    )
  </span>
);

type DynamicShowMoreListProps = {
  // List array to display
  list: Array<string | ReactElement>;
  // Line height a list item (eg, 27 for badges)
  // this is necessary to clip the list to one line, reliably. The container must be of the same height as the list items.
  lineHeight: number;
};

/**
 * A component that displays a list of items, truncating overflow with a "show more"/"show less" button.
 *
 * In order to display cleanly, while collapsed, the list items should expand to a width of 100%. All items must also be a consistent height.
 * In order
 * @example
 * const list = ['Item 1', 'Item 2', 'Item 3'];
 * return (
 *   <DynamicShowMoreList list={list} lineHeight={27} />
 * );
 */
const DynamicShowMoreList: VoidFunctionComponent<DynamicShowMoreListProps> = ({ list, lineHeight }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isShowing, toggleIsShowing] = useToggle(false);
  const [isVisibleList, setIsVisibleList] = useState<boolean[]>([]);

  // Call back for when element is visible on screen
  const onElementVisible = (index: number) => (isVisible: boolean) => {
    setIsVisibleList((prevState) => {
      // Don't update state if it's the same as before
      if (prevState[index] === isVisible) {
        return prevState;
      }

      const newState = [...prevState];
      newState[index] = isVisible;
      return newState;
    });
  };

  const itemList = list.map((listItem, index) => (
    <VisibilityWrapper
      key={_.isString(listItem) ? listItem : listItem?.props.key}
      className={styles.ListItem}
      onElementVisible={onElementVisible(index)}
    >
      {listItem}
    </VisibilityWrapper>
  ));

  const isOverflowing = isVisibleList.some((isVisible) => !isVisible);

  return (
    <div className={cx(styles.showMore, { [styles.showAll]: isShowing })} style={{ maxHeight: `${lineHeight}px` }}>
      <div className={styles.showMoreList} ref={ref}>
        {itemList}
        {isShowing && <ShowMoreButton isShowing={isShowing} toggleIsShowing={toggleIsShowing} />}
      </div>
      {isOverflowing && !isShowing && <ShowMoreButton isShowing={isShowing} toggleIsShowing={toggleIsShowing} />}
    </div>
  );
};

export default DynamicShowMoreList;
